"use client";

import { isToday, isYesterday, subMonths, subWeeks } from "date-fns";
import { motion } from "framer-motion";
import { useParams, useRouter } from "next/navigation";
import type { User } from "next-auth";
import { useState } from "react";
import { toast } from "sonner";
import useSWRInfinite from "swr/infinite";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar"
import { LoaderIcon } from "./icons"
import { ChatItem } from "./sidebar-history-item"
import type { Chat } from "@/lib/types"
import { getChats, deleteChat } from "@/lib/db/queries"

const PAGE_SIZE = 20

const groupChatsByDate = (chats: Chat[]) => {
  const now = new Date()
  const oneWeekAgo = subWeeks(now, 1)
  const oneMonthAgo = subMonths(now, 1)

  return chats.reduce(
    (groups, chat) => {
      const chatDate = new Date(chat.created_at)
      if (isToday(chatDate)) groups.today.push(chat)
      else if (isYesterday(chatDate)) groups.yesterday.push(chat)
      else if (chatDate > oneWeekAgo) groups.lastWeek.push(chat)
      else if (chatDate > oneMonthAgo) groups.lastMonth.push(chat)
      else groups.older.push(chat)
      return groups
    },
    {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    } as Record<string, Chat[]>
  )
}

// SWR key for pagination using Django limit/offset
function getPaginationKey(pageIndex: number, previousPageData: Chat[]) {
  if (previousPageData && previousPageData.length < PAGE_SIZE) return null
  const offset = pageIndex * PAGE_SIZE
  return `/api/chats/?limit=${PAGE_SIZE}&offset=${offset}`
}

export function SidebarHistory({ user }: { user: User | undefined }) {
  const { setOpenMobile } = useSidebar()
  const { id } = useParams()
  const router = useRouter()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const {
    data: pages,
    setSize,
    isValidating,
    isLoading,
    mutate,
  } = useSWRInfinite<Chat[]>(
    getPaginationKey,
    async (key) => {
      const url = key.replace("/api", "") // strip local prefix
      const res = await getChats(PAGE_SIZE, parseInt(new URLSearchParams(url.split("?")[1]).get("offset") || "0"))
      return res.results || res // if paginated DRF response
    },
    { fallbackData: [] }
  )

  const chats = pages ? pages.flat() : []
  const grouped = groupChatsByDate(chats)
  const hasReachedEnd = pages && pages[pages.length - 1]?.length < PAGE_SIZE
  const hasEmptyChatHistory = !isLoading && chats.length === 0

  const handleDelete = async () => {
    if (!deleteId) return
    const promise = deleteChat(deleteId)
    toast.promise(promise, {
      loading: "Deleting chat...",
      success: () => {
        mutate((data) => data?.map((p) => p.filter((c) => c.id !== deleteId)))
        if (deleteId === id) router.push("/")
        return "Chat deleted successfully"
      },
      error: "Failed to delete chat",
    })
    setShowDeleteDialog(false)
  }

  if (!user)
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="flex w-full flex-row items-center justify-center gap-2 px-2 text-sm text-zinc-500">
            Login to save and revisit previous chats!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    )

  if (isLoading)
    return (
      <SidebarGroup>
        <div className="px-2 py-1 text-sidebar-foreground/50 text-xs">
          Today
        </div>
        <SidebarGroupContent>
          <div className="flex flex-col">
            {[44, 32, 28, 64, 52].map((item) => (
              <div
                className="flex h-8 items-center gap-2 rounded-md px-2"
                key={item}
              >
                <div
                  className="h-4 max-w-(--skeleton-width) flex-1 rounded-md bg-sidebar-accent-foreground/10"
                  style={
                    {
                      "--skeleton-width": `${item}%`,
                    } as React.CSSProperties
                  }
                />
              </div>
            ))}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    )

  if (hasEmptyChatHistory)
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="flex w-full flex-row items-center justify-center gap-2 px-2 text-sm text-zinc-500">
            Your conversations will appear here once you start chatting!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    )

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <div className="flex flex-col gap-6">
              {Object.entries(grouped).map(([label, chats]) =>
                chats.length ? (
                  <div key={label}>
                    <div className="px-2 py-1 text-sidebar-foreground/50 text-xs capitalize">
                      {label === "lastWeek"
                        ? "Last 7 days"
                        : label === "lastMonth"
                        ? "Last 30 days"
                        : label === "older"
                        ? "Older than last month"
                        : label}
                    </div>
                    {chats.map((chat) => (
                      <ChatItem
                        key={chat.id}
                        chat={chat}
                        isActive={chat.id === id}
                        setOpenMobile={setOpenMobile}
                        onDelete={(chatId) => {
                          setDeleteId(chatId)
                          setShowDeleteDialog(true)
                        }}
                      />
                    ))}
                  </div>
                ) : null
              )}
            </div>
          </SidebarMenu>

          <motion.div
            onViewportEnter={() => {
              if (!isValidating && !hasReachedEnd) setSize((s) => s + 1)
            }}
          />

          {hasReachedEnd ? (
            <div className="mt-8 flex w-full flex-row items-center justify-center gap-2 px-2 text-sm text-zinc-500">
              You have reached the end of your chat history.
            </div>
          ) : (
            <div className="mt-8 flex flex-row items-center gap-2 p-2 text-zinc-500 dark:text-zinc-400">
              <div className="animate-spin">
                <LoaderIcon />
              </div>
              <div>Loading Chats...</div>
            </div>
          )}
        </SidebarGroupContent>
      </SidebarGroup>

      <AlertDialog onOpenChange={setShowDeleteDialog} open={showDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              chat and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { getPaginationKey as getChatHistoryPaginationKey }
